import React from 'react';

export type BurialSize = 'ADULT' | 'CHILD';

interface BurialSizeOption {
  value: BurialSize;
  label: string;
  description: string;
  dimensions: string;
  actualDimensions: {
    length: number;
    width: number;
    depth: number;
  };
}

interface BurialSizeSelectorProps {
  value: BurialSize;
  onChange: (size: BurialSize) => void;
  onSizeChange?: (size: BurialSize, dimensions: { length: number; width: number; depth: number }) => void;
  disabled?: boolean;
  error?: string;
}

const burialSizes: BurialSizeOption[] = [
  {
    value: 'ADULT',
    label: 'Adult',
    description: 'Standard adult burial plot',
    dimensions: 'Approximately 2.5m x 1.2m',
    actualDimensions: {
      length: 2.5,
      width: 1.2,
      depth: 2.0
    }
  },
  {
    value: 'CHILD',
    label: 'Child',
    description: 'Child burial plot',
    dimensions: 'Approximately 1.8m x 0.9m',
    actualDimensions: {
      length: 1.8,
      width: 0.8,
      depth: 1.5
    }
  },
];

const BurialSizeSelector: React.FC<BurialSizeSelectorProps> = ({
  value,
  onChange,
  onSizeChange,
  disabled = false,
  error,
}) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-4">
          Select Burial Plot Size
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {burialSizes.map((size) => (
            <div
              key={size.value}
              className={`
                relative rounded-lg border-2 p-4 cursor-pointer transition-all
                ${value === size.value
                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                  : 'border-neutral-200 bg-white hover:border-neutral-300'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              onClick={() => {
                if (!disabled) {
                  onChange(size.value);
                  if (onSizeChange) {
                    onSizeChange(size.value, size.actualDimensions);
                  }
                }
              }}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-medium text-neutral-900">
                      {size.label}
                    </h3>
                    {value === size.value && (
                      <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-neutral-600 mt-1">
                    {size.description}
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    {size.dimensions}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 mt-1">
          {error}
        </div>
      )}
    </div>
  );
};

export default BurialSizeSelector;